import React, { useState, useEffect } from 'react';
import { setSEO } from '../lib/seo';

const C = { navy:'#001f3a', navyDark:'#0d1f30', orange:'#f07820', orangeDk:'#9a4600', light:'#f8f9ff', white:'#ffffff' };
const LOGO_W = `${process.env.PUBLIC_URL}/LogoBlanco.png`;

const PAISES = ['México','Colombia','Argentina','Chile','Perú','Ecuador','Venezuela','Bolivia','Paraguay','Uruguay','Guatemala','Honduras','El Salvador','Costa Rica','Panamá','República Dominicana','España','Estados Unidos','Otro'];

export default function WebFeedback() {
  const [form, setForm] = useState({ nombre:'', rol:'', pais:'', texto:'', web:'' });
  const [rating, setRating] = useState(5);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    setSEO({
      title: 'Deja tu testimonio | HireMeUp',
      description: 'Cuéntanos cómo te ayudó HireMeUp en tu camino hacia el trabajo remoto. Tu testimonio puede inspirar a otros profesionales de LATAM.',
      canonical: 'https://hiremeupglobal.com/feedback',
    });
  }, []);

  // Misma normalización que aplica el servidor, para que el contador y las
  // validaciones del formulario coincidan con lo que se valida al recibir.
  const normalizar = t => t.replace(/\s+/g, ' ').trim();

  const ERRORES = {
    nombre_invalido: 'Tu nombre debe tener al menos 2 caracteres.',
    texto_corto: 'Cuéntanos un poco más: el testimonio debe tener al menos 20 caracteres.',
    demasiados_envios: 'Recibimos varios envíos desde tu conexión. Intenta más tarde.',
    error_guardando: 'No pudimos guardar tu testimonio. Intenta de nuevo en unos minutos.',
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const nombre = normalizar(form.nombre);
    const texto  = normalizar(form.texto);
    if (!nombre || !form.pais || !texto) { setError('Completa tu nombre, país y testimonio.'); return; }
    if (nombre.length < 2) { setError(ERRORES.nombre_invalido); return; }
    if (texto.length < 20) { setError(ERRORES.texto_corto); return; }
    setStatus('loading'); setError('');
    try {
      const res = await fetch('/api/feedback', {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ nombre, rol:form.rol, pais:form.pais, texto, rating, web:form.web }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) setStatus('success');
      else { setStatus('error'); setError(ERRORES[data.error] || 'Hubo un error. Intenta de nuevo.'); }
    } catch { setStatus('error'); setError('Error de conexión. Intenta de nuevo.'); }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@700;800;900&family=Inter:wght@400;500;600&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Inter',sans-serif;background:${C.light}}
        .d{font-family:'Manrope',sans-serif} a{text-decoration:none}
        .bo{background:linear-gradient(135deg,${C.orange},${C.orangeDk});color:white;border:none;cursor:pointer;font-family:'Inter',sans-serif;font-weight:700;transition:opacity .18s}
        .bo:hover{opacity:.9}
        .field{width:100%;padding:11px 14px;border-radius:8px;border:1.5px solid rgba(0,31,58,.12);font-size:14px;outline:none;color:${C.navy};background:white;font-family:'Inter',sans-serif}
        .field:focus{border-color:${C.orange}}
        @media(max-width:768px){.fb-grid{flex-direction:column!important}.fb-pad{padding:40px 20px!important}}
      `}</style>

      <nav style={{ background:C.navyDark, padding:'0 40px', height:'56px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <a href="/"><img src={LOGO_W} alt="HireMeUp" style={{ height:'45px', objectFit:'contain' }} /></a>
        <a href="/historias" style={{ fontSize:'13px', color:'rgba(255,255,255,.6)', fontWeight:600 }}>← Volver a historias</a>
      </nav>

      <section className="fb-pad" style={{ minHeight:'calc(100vh - 56px)', background:C.light, display:'flex', alignItems:'center', padding:'60px 40px' }}>
        <div className="fb-grid" style={{ maxWidth:'960px', margin:'0 auto', width:'100%', display:'flex', gap:'64px', alignItems:'center' }}>

          <div style={{ flex:1, minWidth:'260px' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', background:`${C.orange}12`, border:`1px solid ${C.orange}30`, borderRadius:'20px', padding:'5px 14px', marginBottom:'24px' }}>
              <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:C.orange }} />
              <span style={{ fontSize:'11px', fontWeight:700, color:C.orange, letterSpacing:'.1em', textTransform:'uppercase' }}>Ayuda a quien viene detrás</span>
            </div>
            <h1 className="d" style={{ fontSize:'clamp(2rem,4vw,3rem)', fontWeight:900, letterSpacing:'-.03em', lineHeight:1.1, marginBottom:'20px' }}>
              <span style={{ color:C.navy, display:'block' }}>Hoy alguien está</span>
              <span style={{ color:C.orange, display:'block' }}>donde tú estabas.</span>
            </h1>
            <p style={{ fontSize:'15px', lineHeight:1.7, color:'rgba(0,31,58,.6)', marginBottom:'28px', maxWidth:'440px' }}>
              Postulando sin respuesta, dudando de su perfil, sin saber si el trabajo remoto es para gente como él. Leer que a alguien real le funcionó cambia esa conversación. Ese alguien puedes ser tú.
            </p>

            <div style={{ background:'rgba(240,120,32,.06)', border:`1px solid ${C.orange}22`, borderRadius:'14px', padding:'20px 22px', maxWidth:'440px', marginBottom:'20px' }}>
              <p className="d" style={{ fontSize:'13px', fontWeight:800, color:C.navy, marginBottom:'12px' }}>¿No sabes qué escribir? Responde una:</p>
              {['¿Cómo estabas antes de HireMeUp?','¿Qué fue lo que más te sirvió?','¿Qué le dirías a quien está empezando?'].map((q,i) => (
                <div key={i} style={{ display:'flex', gap:'10px', marginBottom:i === 2 ? 0 : '8px' }}>
                  <span style={{ color:C.orange, fontSize:'14px', flexShrink:0, fontWeight:700 }}>→</span>
                  <span style={{ fontSize:'14px', color:'rgba(0,31,58,.7)', lineHeight:1.5 }}>{q}</span>
                </div>
              ))}
            </div>

            <p style={{ fontSize:'13px', color:'rgba(0,31,58,.45)', lineHeight:1.6, maxWidth:'440px' }}>
              Dos minutos y sin registrarte. Solo publicamos tu nombre, tu rol y tu país, y siempre después de revisarlo.
            </p>
          </div>

          <div style={{ flex:'0 0 400px', minWidth:'280px', width:'100%' }}>
            <div style={{ background:C.white, borderRadius:'20px', padding:'36px 32px', boxShadow:'0 16px 48px rgba(0,31,58,.08)', border:'1px solid rgba(0,31,58,.06)' }}>
              {status === 'success' ? (
                <div style={{ textAlign:'center', padding:'20px 0' }}>
                  <div style={{ width:'64px', height:'64px', borderRadius:'50%', background:'rgba(15,118,110,.1)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                    <span style={{ fontSize:'28px', color:'#0f766e' }}>✓</span>
                  </div>
                  <h3 className="d" style={{ fontSize:'20px', fontWeight:800, color:C.navy, marginBottom:'8px' }}>Gracias, de verdad.</h3>
                  <p style={{ fontSize:'14px', color:'rgba(0,31,58,.55)', lineHeight:1.6, marginBottom:'16px' }}>
                    Lo revisamos y en cuanto se apruebe aparece publicado en la web. Ahí va a estar cuando alguien lo necesite.
                  </p>
                  <a href="/historias" style={{ fontSize:'13px', color:C.orange, fontWeight:600 }}>← Ver historias</a>
                </div>
              ) : (
                <>
                  <h2 className="d" style={{ fontSize:'20px', fontWeight:800, color:C.navy, marginBottom:'6px' }}>Cuenta tu historia</h2>
                  <p style={{ fontSize:'13px', color:'rgba(0,31,58,.45)', marginBottom:'24px' }}>Unas líneas bastan. No hace falta que sea perfecto.</p>
                  <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
                    <div>
                      <label style={{ fontSize:'12px', fontWeight:600, color:'rgba(0,31,58,.6)', display:'block', marginBottom:'6px' }}>Nombre</label>
                      <input type="text" value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} placeholder="Tu nombre" className="field" maxLength={80} />
                    </div>
                    <div>
                      <label style={{ fontSize:'12px', fontWeight:600, color:'rgba(0,31,58,.6)', display:'block', marginBottom:'6px' }}>Rol o profesión <span style={{ fontWeight:400, color:'rgba(0,31,58,.35)' }}>(opcional)</span></label>
                      <input type="text" value={form.rol} onChange={e => setForm({...form, rol:e.target.value})} placeholder="Ej: Diseñadora UX" className="field" maxLength={120} />
                    </div>
                    <div>
                      <label style={{ fontSize:'12px', fontWeight:600, color:'rgba(0,31,58,.6)', display:'block', marginBottom:'6px' }}>País</label>
                      <select value={form.pais} onChange={e => setForm({...form, pais:e.target.value})} className="field">
                        <option value="">Selecciona tu país</option>
                        {PAISES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize:'12px', fontWeight:600, color:'rgba(0,31,58,.6)', display:'block', marginBottom:'6px' }}>Tu experiencia con HireMeUp</label>
                      <div style={{ display:'flex', gap:'4px', marginBottom:'8px' }}>
                        {[1,2,3,4,5].map(s => (
                          <button key={s} type="button" onClick={() => setRating(s)} aria-label={`${s} estrellas`}
                            style={{ background:'none', border:'none', cursor:'pointer', fontSize:'24px', padding:'0 2px', color:s <= rating ? C.orange : 'rgba(0,31,58,.15)', transition:'color .15s' }}>★</button>
                        ))}
                      </div>
                      <textarea value={form.texto} onChange={e => setForm({...form, texto:e.target.value})} placeholder="Ej: Reescribí mi CV con la metodología y en tres semanas ya tenía entrevistas." className="field" rows={4} maxLength={600} style={{ resize:'vertical' }} />
                      <p style={{ fontSize:'11px', color:'rgba(0,31,58,.3)', textAlign:'right', marginTop:'4px' }}>{normalizar(form.texto).length}/600</p>
                    </div>
                    <input type="text" value={form.web} onChange={e => setForm({...form, web:e.target.value})} name="web" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position:'absolute', left:'-5000px', width:'1px', height:'1px', opacity:0 }} />
                    {error && <p style={{ fontSize:'12px', color:'#dc2626', background:'#fef2f2', padding:'8px 12px', borderRadius:'6px' }}>{error}</p>}
                    <button type="submit" disabled={status==='loading'} className="bo"
                      style={{ padding:'13px', borderRadius:'10px', fontSize:'14px', marginTop:'4px', opacity:status==='loading'?.7:1, width:'100%' }}>
                      {status==='loading'?'Enviando...':'Compartir mi historia →'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
